import type {
  AssessmentQuestion,
  AssessmentVisualCue,
  Unit,
  LearningLayer,
  LessonConfig,
  LayerStrategy,
  Badge,
  SentencePattern,
} from '@/types'


// ========================
// 分层配置 — 核心自适应逻辑
// ========================

export const layerLessonConfigs: Record<LearningLayer, LessonConfig> = {
  A: {
    layer: 'A',
    skipInput: true,
    inputRounds: 0,
    outputLevel: 'challenge',
    showHints: false,
    spellingMode: 'full',
    sentenceMode: 'create',
    textMode: 'recite-extend',
    phonicsMode: 'cvc-extend',
  },
  B: {
    layer: 'B',
    skipInput: false,
    inputRounds: 1,
    outputLevel: 'standard',
    showHints: false,
    spellingMode: 'fill-blank',
    sentenceMode: 'arrange',
    textMode: 'read-arrange-recite',
    phonicsMode: 'standard',
  },
  C: {
    layer: 'C',
    skipInput: false,
    inputRounds: 2,
    outputLevel: 'guided',
    showHints: true,
    spellingMode: 'select-letter',
    sentenceMode: 'fill-blank',
    textMode: 'guided-read',
    phonicsMode: 'extra-practice',
  },
}

export const layerStrategies: Record<LearningLayer, LayerStrategy> = {
  A: {
    name: 'A层 - 领航探险家',
    scoreRange: '8-10分',
    description: '输入环节压缩，简化操练，输出难度提升',
    color: 'mint',
    vocabStrategy: '可跳过输入环节，直接进行拼写和运用挑战',
    sentenceStrategy: '自主造句，情景交际，灵活替换',
    textStrategy: '课文背诵+拓展运用',
    phonicsStrategy: 'CVC拼读拓展，尝试拼读新词',
    lessonConfig: layerLessonConfigs.A,
  },
  B: {
    name: 'B层 - 追光探险家',
    scoreRange: '4-7分',
    description: '标准路径：完整输入→游戏操练→半控制输出',
    color: 'brand',
    vocabStrategy: '标准学习路径：单词卡→听音选图→跟读→拼写',
    sentenceStrategy: '句型视频→选图→连词成句',
    textStrategy: '课文跟读→排序→背诵',
    phonicsStrategy: '律动视频→听音选词→读单词',
    lessonConfig: layerLessonConfigs.B,
  },
  C: {
    name: 'C层 - 新芽探险家',
    scoreRange: '0-3分',
    description: '强化输入，输出降级',
    color: 'warm',
    vocabStrategy: '强化输入：多看多听，辅助提示完成拼写',
    sentenceStrategy: '增加提示和引导，降低输出难度',
    textStrategy: '多轮跟读，图片辅助理解',
    phonicsStrategy: '增加跟读次数，重点掌握基础发音',
    lessonConfig: layerLessonConfigs.C,
  },
}

// ========================
// 检测引擎 — 从单元内容自动生成题目
// ========================

export function generateAssessmentQuestions(
  unit: Unit,
  layer: LearningLayer,
  variantIndex = 0,
): AssessmentQuestion[] {
  const questions: AssessmentQuestion[] = []
  const variant = Math.max(0, Math.floor(variantIndex))
  const variantSeed = (seed: string) => `${seed}-variant-${variant}`
  const hintLevel: 0 | 1 | 2 = layer === 'A' ? 0 : layer === 'B' ? 1 : 2

  // 单词板块 — 听音选图
  unit.vocab.slice(0, 3).forEach((v, i) => {
    const choice = createChoiceOptions(
      v.meaning,
      unit.vocab.map((word) => word.meaning),
      variantSeed(`${unit.id}-q-vocab-${i}`),
    )
    questions.push({
      id: `${unit.id}-q-vocab-${i}`,
      version: 1,
      variant,
      type: 'q1',
      unitId: unit.id,
      category: '单词',
      subCategory: '听音选图',
      ability: 'listening',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '单词', v.word),
      frontendType: '单选题',
      content: {
        prompt: `听音选图：播放 "${v.word}" 的音频`,
        audioText: v.word,
        options: choice.options,
        optionVisuals: choice.options.map((meaning) => createVocabVisual(
          unit.vocab.find((word) => word.meaning === meaning)?.word ?? meaning,
          meaning,
        )),
        correctAnswer: choice.correctAnswer,
        hintLevel,
      },
      scoring: { maxScore: 10, method: 'exact' },
    })
  })

  // 单词板块 — 看图说词
  if (unit.vocab.length > 0) {
    const target = unit.vocab[Math.min(1, unit.vocab.length - 1)]
    const visual = createVocabVisual(target.word, target.meaning)
    questions.push({
      id: `${unit.id}-q-vocab-speak`,
      version: 1,
      variant,
      type: 'q2',
      unitId: unit.id,
      category: '单词',
      subCategory: '看图说词',
      ability: 'speaking',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '看图说词', target.word),
      frontendType: '口语题',
      content: {
        prompt: '观察图示，说出对应的英文单词。',
        imageUrl: assessmentVisualImages[visual.key],
        visual,
        correctAnswer: target.word,
        hintLevel,
      },
      scoring: { maxScore: 10, partialCredit: true, method: 'speech-recognition' },
    })
  }

  // 单词板块 — 单词拼写（按层级调整难度）
  unit.vocab.slice(0, 2).forEach((v, i) => {
    const mode = layerLessonConfigs[layer].spellingMode
    let prompt = ''
    let answer: string | number = ''
    let options: string[] | undefined
    if (mode === 'full') {
      prompt = `看中文拼写完整单词：${v.meaning}`
      answer = v.word
    } else if (mode === 'fill-blank') {
      const blanked = v.word.replace(/[aeiou]/g, '_')
      prompt = `选择能补全单词的答案：${blanked}（${v.meaning}）`
      const choice = createChoiceOptions(
        v.word,
        unit.vocab.map((word) => word.word),
        variantSeed(`${unit.id}-q-spell-${i}-fill`),
      )
      options = choice.options
      answer = choice.correctAnswer
    } else {
      prompt = `选择首字母补全单词：___${v.word.slice(1)}（${v.meaning}）`
      const choice = createChoiceOptions(
        v.word[0],
        [...unit.vocab.map((word) => word.word[0]), 'b', 'm', 's'],
        variantSeed(`${unit.id}-q-spell-${i}-initial`),
      )
      options = choice.options
      answer = choice.correctAnswer
    }
    questions.push({
      id: `${unit.id}-q-spell-${i}`,
      version: 1,
      variant,
      type: 'q3',
      unitId: unit.id,
      category: '单词',
      subCategory: '单词拼写',
      ability: 'writing',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '单词拼写', v.word),
      frontendType: mode === 'full' ? '填空题' : '单选题',
      content: {
        prompt,
        options,
        correctAnswer: answer,
        hintLevel,
      },
      scoring: { maxScore: 10, method: 'exact' },
    })
  })

  // 句型板块 — 听句子选图
  const sentenceCandidates = unit.sentences
    .flatMap((sentence) => sentence.examples)
    .filter(Boolean)

  if (sentenceCandidates.length > 0) {
    const targetSentence = sentenceCandidates[variant % sentenceCandidates.length]
    const choice = createChoiceOptions(
      targetSentence,
      sentenceCandidates,
      variantSeed(`${unit.id}-q-sentence-listen`),
    )
    questions.push({
      id: `${unit.id}-q-sentence-listen`,
      version: 1,
      variant,
      type: 'q4',
      unitId: unit.id,
      category: '句型',
      subCategory: '听句子选图',
      ability: 'listening',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '听句子选图', unit.sentences[0].pattern),
      frontendType: '单选题',
      content: {
        prompt: '听句子，选择最符合内容的场景图。',
        audioText: targetSentence,
        options: choice.options,
        optionVisuals: choice.options.map((sentence) => createSentenceVisual(sentence, unit)),
        correctAnswer: choice.correctAnswer,
        hintLevel,
      },
      scoring: { maxScore: 10, method: 'exact' },
    })
  }

  // 句型板块 — 中英互译
  unit.sentences.slice(0, 2).forEach((s, i) => {
    if (s.examples.length === 0) return
    const ex = s.examples[variant % s.examples.length]
    const choice = createChoiceOptions(
      getTranslation(ex, s),
      ['这是另一种表达', '句意与原文不符', '这句话描述的是其他场景'],
      variantSeed(`${unit.id}-q-sent-${i}`),
    )
    questions.push({
      id: `${unit.id}-q-sent-${i}`,
      version: 1,
      variant,
      type: 'q5',
      unitId: unit.id,
      category: '句型',
      subCategory: '中英互译',
      ability: 'reading',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '句型', s.pattern),
      frontendType: '单选题',
      content: {
        prompt: `选择正确翻译："${ex}"`,
        options: choice.options,
        correctAnswer: choice.correctAnswer,
        hintLevel,
      },
      scoring: { maxScore: 10, method: 'exact' },
    })
  })

  // 句型板块 — 看图说话
  if (sentenceCandidates.length > 0) {
    const sentence = sentenceCandidates[variant % sentenceCandidates.length]
    questions.push({
      id: `${unit.id}-q-sentence-speak`,
      version: 1,
      variant,
      type: 'q6',
      unitId: unit.id,
      category: '句型',
      subCategory: '看图说话',
      ability: 'speaking',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '看图说话', unit.sentences[0].pattern),
      frontendType: '口语题',
      content: {
        prompt: '根据场景图，用本单元句型说一句完整的英文。',
        visual: createSentenceVisual(sentence, unit),
        correctAnswer: sentence,
        hintLevel,
      },
      scoring: { maxScore: 10, partialCredit: true, method: 'speech-recognition' },
    })
  }

  // 句型板块 — 连词成句
  if (sentenceCandidates.length > 0) {
    const ex = sentenceCandidates[variant % sentenceCandidates.length]
    const words = ex.replace(/[.!?,]/g, '').split(' ')
    questions.push({
      id: `${unit.id}-q-arrange`,
      version: 1,
      variant,
      type: 'q7',
      unitId: unit.id,
      category: '句型',
      subCategory: '连词成句',
      ability: 'writing',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '句型', unit.sentences[0].pattern),
      frontendType: '排序题',
      content: {
        prompt: `将以下单词排列成正确的句子：`,
        options: shuffleArray(words, variantSeed(`${unit.id}-q-arrange`)),
        correctAnswer: words.join(' '),
        hintLevel,
      },
      scoring: { maxScore: 10, method: 'exact' },
    })
  }

  // 课文板块 — 朗读与中译英口语
  if (unit.text.partA) {
    const readingSentences = getTextSentences(unit.text.partA)
    const readingSentence = readingSentences[variant % readingSentences.length]
      ?? getFirstTextSentence(unit.text.partA)
    const speakingExamples = unit.sentences[0]?.examples ?? []
    const speakingSentence = speakingExamples[variant % speakingExamples.length] ?? readingSentence
    questions.push({
      id: `${unit.id}-q-text-read`,
      version: 1,
      variant,
      type: 'q8',
      unitId: unit.id,
      category: '课文',
      subCategory: '读句子',
      ability: 'speaking',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '课文朗读', 'part-a'),
      frontendType: '口语题',
      content: {
        prompt: `朗读句子：${readingSentence}`,
        audioText: readingSentence,
        correctAnswer: readingSentence,
        hintLevel,
      },
      scoring: { maxScore: 10, partialCredit: true, method: 'speech-recognition' },
    })
    questions.push({
      id: `${unit.id}-q-text-translate-speak`,
      version: 1,
      variant,
      type: 'q9',
      unitId: unit.id,
      category: '课文',
      subCategory: '看中文说句子',
      ability: 'speaking',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '中译英口语', unit.sentences[0]?.pattern ?? 'part-a'),
      frontendType: '口语题',
      content: {
        prompt: `用英文说出：${getTranslation(speakingSentence, unit.sentences[0])}`,
        audioText: speakingSentence,
        correctAnswer: speakingSentence,
        hintLevel,
      },
      scoring: { maxScore: 10, partialCredit: true, method: 'speech-recognition' },
    })

    const lines = unit.text.partA.split('\n').filter(l => l.trim())
    questions.push({
      id: `${unit.id}-q-text-sort`,
      version: 1,
      variant,
      type: 'q10',
      unitId: unit.id,
      category: '课文',
      subCategory: '课文排序',
      ability: 'reading',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '课文', 'part-a-order'),
      frontendType: '排序题',
      content: {
        prompt: '将课文句子按正确顺序排列：',
        options: shuffleArray(lines, variantSeed(`${unit.id}-q-text-sort`)),
        correctAnswer: lines.join('\n'),
        hintLevel,
      },
      scoring: { maxScore: 10, method: 'exact' },
    })
  }

  // 自拼板块 — 听音选词
  if (unit.phonics.words.length > 0) {
    const phonicsAnswer = unit.phonics.words[variant % unit.phonics.words.length]
      ?? unit.phonics.words[0]
    const choice = createChoiceOptions(
      phonicsAnswer,
      [...unit.phonics.words, ...unit.vocab.map((word) => word.word)],
      variantSeed(`${unit.id}-q-phonics`),
    )
    questions.push({
      id: `${unit.id}-q-phonics`,
      version: 1,
      variant,
      type: 'q11',
      unitId: unit.id,
      category: '自拼',
      subCategory: '听音选词',
      ability: 'listening',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '自拼', unit.phonics.letter),
      frontendType: '单选题',
      content: {
        prompt: `听字母 "${unit.phonics.letter}" 的发音，选出包含该发音的单词`,
        audioText: unit.phonics.letter,
        options: choice.options,
        correctAnswer: choice.correctAnswer,
        hintLevel,
      },
      scoring: { maxScore: 10, method: 'exact' },
    })

    const readingWord = unit.phonics.words[(variant + 1) % unit.phonics.words.length]
      ?? unit.phonics.words[0]
    questions.push({
      id: `${unit.id}-q-phonics-read`,
      version: 1,
      variant,
      type: 'q12',
      unitId: unit.id,
      category: '自拼',
      subCategory: '读单词',
      ability: 'speaking',
      knowledgePointId: createAssessmentKnowledgePointId(unit.id, '自然拼读朗读', unit.phonics.letter),
      frontendType: '口语题',
      content: {
        prompt: `读出单词：${readingWord}`,
        audioText: readingWord,
        visual: createVocabVisual(readingWord, readingWord),
        correctAnswer: readingWord,
        hintLevel,
      },
      scoring: { maxScore: 10, partialCredit: true, method: 'speech-recognition' },
    })
  }

  return questions
}

function hashSeed(value: string): number {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function shuffleArray<T>(items: readonly T[], seedText: string): T[] {
  const result = [...items]
  let seed = hashSeed(seedText) || 1
  for (let index = result.length - 1; index > 0; index -= 1) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
    const swapIndex = seed % (index + 1)
    ;[result[index], result[swapIndex]] = [result[swapIndex], result[index]]
  }
  return result
}

function createChoiceOptions(correct: string, candidates: readonly string[], seed: string) {
  const distractors = [...new Set(candidates)]
    .filter((candidate) => candidate && candidate !== correct)
    .slice(0, 3)
  const options = shuffleArray([correct, ...distractors], seed)
  return { options, correctAnswer: options.indexOf(correct) }
}

const assessmentVisualRules: { terms: string[]; key: string }[] = [
  { terms: ['classroom', '教室'], key: 'classroom' },
  { terms: ['library', '图书馆', 'book', 'story', 'read'], key: 'library' },
  { terms: ['playground', '操场'], key: 'playground' },
  { terms: ['music', '音乐'], key: 'music' },
  { terms: ['art', '美术'], key: 'art' },
  { terms: ['computer', '电脑'], key: 'computer' },
  { terms: ['office', '办公室'], key: 'office' },
  { terms: ['school', '学校'], key: 'school' },
  { terms: ['canteen', 'lunch', 'breakfast', 'dinner', '餐厅', '午餐', '早餐', '晚餐'], key: 'meal' },
  { terms: ['family', '家庭'], key: 'family' },
  { terms: ['father', 'dad', '爸爸', '父亲'], key: 'father' },
  { terms: ['mother', 'mum', '妈妈', '母亲'], key: 'mother' },
  { terms: ['brother', '兄弟'], key: 'brother' },
  { terms: ['sister', '姐妹'], key: 'sister' },
  { terms: ['grandfather', 'grandpa', '祖父', '爷爷'], key: 'grandfather' },
  { terms: ['grandmother', 'grandma', '祖母', '奶奶'], key: 'grandmother' },
  { terms: ['hello', 'hi', 'goodbye', '你好', '再见'], key: 'greeting' },
  { terms: ['name', '名字'], key: 'name' },
  { terms: ['friend', 'meet', '朋友', '遇见'], key: 'friends' },
  { terms: ['pencil', '铅笔'], key: 'pencil' },
  { terms: ['pen', '钢笔'], key: 'pen' },
  { terms: ['ruler', '尺子'], key: 'ruler' },
  { terms: ['eraser', '橡皮'], key: 'eraser' },
  { terms: ['bag', '书包'], key: 'bag' },
  { terms: ['dog', '狗'], key: 'dog' },
  { terms: ['cat', '猫'], key: 'cat' },
  { terms: ['bird', '鸟'], key: 'bird' },
  { terms: ['fish', '鱼'], key: 'fish' },
  { terms: ['rabbit', '兔子'], key: 'rabbit' },
  { terms: ['duck', '鸭子'], key: 'duck' },
  { terms: ['apple', '苹果'], key: 'apple' },
  { terms: ['banana', '香蕉'], key: 'banana' },
  { terms: ['orange', '橙子'], key: 'orange' },
  { terms: ['pear', '梨'], key: 'pear' },
  { terms: ['grape', '葡萄'], key: 'grape' },
  { terms: ['watermelon', '西瓜'], key: 'watermelon' },
  { terms: ['clock', 'time', "o'clock", '时间', '时钟'], key: 'clock' },
  { terms: ['morning', '早上'], key: 'morning' },
  { terms: ['afternoon', '下午'], key: 'afternoon' },
  { terms: ['evening', 'night', '晚上', '夜晚'], key: 'night' },
  { terms: ['lion', '狮子'], key: 'lion' },
]

const assessmentVisualDescriptions: Record<string, string> = {
  playground: '孩子们在校园户外跑步、玩球和放风筝的运动场景',
}

const assessmentVisualImages: Record<string, string> = {
  playground: '/textbook/page-07.png',
}

function createVocabVisual(word: string, meaning: string): AssessmentVisualCue {
  const searchable = `${word} ${meaning}`.toLowerCase()
  const rule = assessmentVisualRules.find(({ terms }) => (
    terms.some((term) => searchable.includes(term.toLowerCase()))
  ))
  const key = rule?.key ?? 'word'
  return { key, alt: assessmentVisualDescriptions[key] ?? meaning ?? word }
}

function createSentenceVisual(sentence: string, unit: Unit): AssessmentVisualCue {
  const searchable = sentence.toLowerCase()
  const vocab = [...unit.vocab]
    .sort((left, right) => right.word.length - left.word.length)
    .find((item) => searchable.includes(item.word.toLowerCase().replace(/s$/, '')))
  if (vocab) return createVocabVisual(vocab.word, vocab.meaning)
  if (/what'?s your name|my name/i.test(sentence)) return { key: 'name', alt: '介绍姓名的场景' }
  if (/nice to meet|who'?s/i.test(sentence)) return { key: 'friends', alt: '见面交谈的场景' }
  if (/thank you/i.test(sentence)) return { key: 'greeting', alt: '礼貌交流的场景' }
  return { key: 'conversation', alt: `${unit.theme}交流场景` }
}

function getFirstTextSentence(text: string): string {
  const firstLine = text.split('\n').map((line) => line.trim()).find(Boolean) ?? text.trim()
  return firstLine.replace(/^[A-Z]\s*:\s*/i, '').trim()
}

function getTextSentences(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim().replace(/^[A-Z]\s*:\s*/i, '').trim())
    .filter(Boolean)
}

function createAssessmentKnowledgePointId(unitId: string, category: string, target: string): string {
  const normalized = `${category}-${target}`
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\u4e00-\u9fff-]/g, '')
  return `${unitId}:${normalized}:v1`
}

function getTranslation(en: string, pattern?: SentencePattern): string {
  const map: Record<string, string> = {
    'Welcome to school!': '欢迎来到学校！',
    'This is our classroom.': '这是我们的教室',
    'This is our playground.': '这是我们的操场',
    'This is our library.': '这是我们的图书馆',
    'That is the playground.': '那是操场',
    'That is the music room.': '那是音乐教室',
    'This is my father.': '这是我的爸爸',
    'This is my mother.': '这是我的妈妈',
    'This is my brother.': '这是我的兄弟',
    "Who's he? He's my brother.": '他是谁？他是我的兄弟。',
    "Who's she? She's my sister.": '她是谁？她是我的姐妹。',
    'Nice to meet you.': '很高兴认识你。',
    'Nice to meet you, too.': '我也很高兴认识你。',
    "Hello! I'm Peter.": '你好！我是彼得。',
    "Hello! I'm Lingling.": '你好！我是玲玲。',
    "What's your name? My name is Tom.": '你叫什么名字？我叫汤姆。',
    "What's your name? My name is Lily.": '你叫什么名字？我叫莉莉。',
    'Is this your pen? Yes, it is.': '这是你的钢笔吗？是的。',
    'Is this your ruler? No, it isn\'t.': '这是你的尺子吗？不是。',
    "What's this? It's a pencil.": '这是什么？这是一支铅笔。',
    "What's this? It's a book.": '这是什么？这是一本书。',
    "What's this? It's a dog.": '这是什么？这是一只狗。',
    "What's this? It's a cat.": '这是什么？这是一只猫。',
    'Is it a bird? Yes, it is.': '它是一只鸟吗？是的。',
    'I like apples.': '我喜欢苹果。',
    'I like bananas.': '我喜欢香蕉。',
    'Do you like pears? Yes, I do.': '你喜欢梨吗？是的，我喜欢。',
    "What time is it? It's seven o'clock.": '几点了？七点。',
    'I have breakfast at seven.': '我七点吃早餐。',
    "It's time to go to school.": '该去上学了。',
  }
  return map[en]
    || pattern?.cnExamples?.[pattern.examples.indexOf(en)]
    || (pattern ? `本单元句型：${pattern.pattern}` : '请用英文复述这句话')
}

// ========================
// 检测报告生成
// ========================

// ========================
// 成就徽章系统
// ========================

export const badges: Badge[] = [
  { id: 'b1', name: '初出茅庐', icon: '🌱', condition: '完成首次前测', earned: false },
  { id: 'b2', name: '勤学不辍', icon: '🔥', condition: '连续学习7天', earned: false },
  { id: 'b3', name: '月度坚持', icon: '🌙', condition: '连续学习30天', earned: false },
  { id: 'b4', name: '单词达人', icon: '📖', condition: '累计掌握100个单词', earned: false },
  { id: 'b5', name: '句型高手', icon: '💬', condition: '完成50个句型练习', earned: false },
  { id: 'b6', name: '朗读之星', icon: '🎤', condition: '口语评测平均分≥90', earned: false },
  { id: 'b7', name: '全能探险家', icon: '🏆', condition: '完成所有单元检测', earned: false },
  { id: 'b8', name: '满分学霸', icon: '👑', condition: '单元检测满分', earned: false },
]
